import React, { memo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import URLSearchParams from "url-search-params";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { ConfigProvider } from "antd";
import { IntlProvider } from "react-intl";

import AppLocale from "lngProvider";
import MainApp from "./MainApp";
import SignIn from "../SignIn";
// import SignUp from "../SignUp";
import ForgetPassword from "../ForgetPassword";
import PayWithoutLogin from "../PayWithoutLogin";
import ResetPassword from "../ResetPassword";
import CreateNewPassword from "../NewPassword";
import PosCollectionResult from "../PosCollectionResult";
import { setInitUrl } from "appRedux/actions/Auth";
import {
  onLayoutTypeChange,
  onNavStyleChange,
  setThemeType,
} from "appRedux/actions/Setting";

import {
  LAYOUT_TYPE_BOXED,
  LAYOUT_TYPE_FRAMED,
  LAYOUT_TYPE_FULL,
  NAV_STYLE_ABOVE_HEADER,
  NAV_STYLE_BELOW_HEADER,
  NAV_STYLE_DARK_HORIZONTAL,
  NAV_STYLE_DEFAULT_HORIZONTAL,
  NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
} from "../../constants/ThemeSetting";
import "moment/locale/tr";
import localetr from "antd/lib/locale/tr_TR";
import PaymentPage from "../PaymentPage";
import { PageProvider } from "../Context/PageContext";

const RestrictedRoute = ({
  component: Component,
  location,
  authUser,
  ...rest
}) => (
  <Route
    {...rest}
    render={(props) =>
      authUser ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: "/signin",
            state: { from: location },
          }}
        />
      )
    }
  />
);

const App = (props) => {
  const dispatch = useDispatch();
  const { locale, navStyle, layoutType } = useSelector(
    ({ settings }) => settings
  );
  const { authUser, initURL } = useSelector(({ auth }) => auth);

  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  useEffect(() => {
    if (initURL === "") {
      dispatch(setInitUrl(location.pathname));
    }
    const params = new URLSearchParams(location.search);

    if (params.has("theme")) {
      dispatch(setThemeType(params.get("theme")));
    }
    if (params.has("nav-style")) {
      dispatch(onNavStyleChange(params.get("nav-style")));
    }
    if (params.has("layout-type")) {
      dispatch(onLayoutTypeChange(params.get("layout-type")));
    }
    setLayoutType(layoutType);
    setNavStyle(navStyle);
  });

  const setLayoutType = (layoutType) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
      document.body.classList.remove("boxed-layout");
      document.body.classList.remove("framed-layout");
      document.body.classList.add("full-layout");
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
      document.body.classList.remove("full-layout");
      document.body.classList.remove("framed-layout");
      document.body.classList.add("boxed-layout");
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
      document.body.classList.remove("boxed-layout");
      document.body.classList.remove("full-layout");
      document.body.classList.add("framed-layout");
    }
  };

  const setNavStyle = (navStyle) => {
    if (
      navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
      navStyle === NAV_STYLE_DARK_HORIZONTAL ||
      navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
      navStyle === NAV_STYLE_ABOVE_HEADER ||
      navStyle === NAV_STYLE_BELOW_HEADER
    ) {
      document.body.classList.add("full-scroll");
      document.body.classList.add("horizontal-layout");
    } else {
      document.body.classList.remove("full-scroll");
      document.body.classList.remove("horizontal-layout");
    }
  };

  useEffect(() => {
    if (location.pathname === "/") {
      if (authUser === null) {
        history.push("/signin");
      } else if (initURL === "" || initURL === "/" || initURL === "/signin") {
        history.push("/home");
      } else {
        history.push(initURL);
      }
    }
  }, [authUser, initURL, location, history]);

  useEffect(() => {
    const checkVersion = async () => {
      try {
      
        const response = await fetch(`/meta.json?t=${new Date().getTime()}`, {
          headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
        });

        const meta = await response.json();
        const localVersion = process.env.REACT_APP_VERSION; 
        const serverVersion = meta.version;

        console.log(`Local: ${localVersion} - Server: ${serverVersion}`);

      
        if (serverVersion && localVersion !== serverVersion) {
          console.warn("⚡ KESİN GÜNCELLEME BAŞLATILIYOR...");

       
          if ("caches" in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
          }

      
          if ("serviceWorker" in navigator) {
            const registrations =
              await navigator.serviceWorker.getRegistrations();
            for (let registration of registrations) {
              await registration.unregister();
            }
          }

     
          const url = new URL(window.location.href);

          
          url.searchParams.set("v", serverVersion);

          window.location.replace(url.toString());
        }
      } catch (error) {
        console.error("Versiyon kontrol hatası:", error);
      }
    };

    checkVersion();

  
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkVersion();
    });
  }, [location]);

  const currentAppLocale = AppLocale[locale.locale];

  return (
    <ConfigProvider locale={localetr}>
      <IntlProvider
        locale={currentAppLocale.locale}
        messages={currentAppLocale.messages}
      >
        <PageProvider>
          <Switch>
            <Route exact path="/signin" component={SignIn} />
            {/* <Route exact path="/signup" component={SignUp} /> */}
            <Route exact path="/forget-password" component={ForgetPassword} />
            <Route
              exact
              path="/pay-without-login/:amount?/:firmbranchNumber?/:referenceNumber?/:callbackUrl?"
              component={PayWithoutLogin}
            />
            <Route
              exact
              path="/reset-password/:psrGuid"
              component={ResetPassword}
            />
            <Route
              path={`${match.url}new-password/:psrGuid`}
              component={CreateNewPassword}
            />
            <Route
              path={`${match.url}pos-collection-result/:success/:message`}
              component={PosCollectionResult}
            />
            <Route exact path="/paymentPage" component={PaymentPage} />

            <RestrictedRoute
              path={`${match.url}`}
              authUser={authUser}
              location={location}
              component={MainApp}
            />
          </Switch>
        </PageProvider>
      </IntlProvider>
    </ConfigProvider>
  );
};

export default memo(App);
